import { useEffect, useState } from 'react';

import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';
import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useImportRetailEvotorDevices, useRetailEvotorDevices } from '../../hooks';

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export interface RetailEvotorDevicesPanelProps {
  canManage: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Кассы (devices) tab — a self-contained, read-only mirror of the smart terminals (kassas) linked
 * to the tenant's Evotor accounts, structured like `RetailEvotorStoresPanel` (search + refresh +
 * `LinearProgress` background-refetch indicator + `CompactTablePagination` + all four content
 * states). The only mutation here is the "Загрузить из Эвотор" header action — devices are
 * otherwise read-only on this platform (imported, never edited).
 *
 * A merchant identifies a till by its serial number, not its Evotor id, so the Serial number
 * column carries the scannable/searchable value; IMEI rides along underneath it for the terminals
 * that report one.
 */
export function RetailEvotorDevicesPanel({
  canManage,
  onSuccess,
  onError,
}: RetailEvotorDevicesPanelProps) {
  const { t } = useTranslation('settings');
  const [filterInput, setFilterInput] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const query = useRetailEvotorDevices({
    name: filterInput.trim() || undefined,
    page,
    size,
    sort: 'updatedAt:desc',
  });
  const importMutation = useImportRetailEvotorDevices();

  const data = query.data;
  const devices = data?.content ?? [];
  const hasFilter = filterInput.trim() !== '';
  const showLinearProgress = query.isFetching && !query.isLoading;

  // Same page-clamp rationale as `RetailEvotorStoresPanel`/`RetailEvotorAccountsPanel` — keep
  // `page` in range after the last device on the last page stops matching (e.g. a narrower filter).
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(0, Math.ceil(data.page.totalElements / size) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [data, page, size]);

  // Tenant-scoped (adr-integration-012) and reversible — runs immediately, no confirmation,
  // mirroring the Stores tab's own "Загрузить из Эвотор" action.
  const runImport = () => {
    importMutation.mutate(undefined, {
      onSuccess: () => onSuccess(t('retailSettings.devices.importStarted')),
      onError: (error) => onError(error.message),
    });
  };

  const importButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        importMutation.isPending ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <PointOfSaleRoundedIcon />
        )
      }
      disabled={!canManage || importMutation.isPending}
      onClick={runImport}
    >
      {t('retailSettings.devices.importButton')}
    </Button>
  );

  const action = canManage ? (
    importButton
  ) : (
    <Tooltip title={t('retailSettings.noPermissionTooltip')}>
      <span>{importButton}</span>
    </Tooltip>
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{t('retailSettings.devices.cardTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('retailSettings.devices.cardDescription')}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>{action}</Box>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pb: 1.5 }}>
        <TextField
          size="small"
          value={filterInput}
          onChange={(event) => {
            setFilterInput(event.target.value);
            setPage(0);
          }}
          placeholder={t('retailSettings.devices.searchPlaceholder')}
          aria-label={t('retailSettings.devices.searchPlaceholder')}
          sx={{ flexGrow: 1, maxWidth: 320 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Tooltip title={t('retailSettings.accounts.refreshAria')}>
          <IconButton
            aria-label={t('retailSettings.accounts.refreshAria')}
            onClick={() => void query.refetch()}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ height: 4 }}>
        {showLinearProgress ? (
          <LinearProgress aria-label={t('retailSettings.refreshingAria')} sx={{ height: 4 }} />
        ) : null}
      </Box>

      {query.isLoading ? (
        <LoadingState label={t('scaffold.loading')} fullHeight={false} />
      ) : query.isError ? (
        <ErrorState
          title={t('retailSettings.devices.loadError')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      ) : devices.length === 0 ? (
        hasFilter ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('retailSettings.accounts.noMatches', { filter: filterInput })}
          </Typography>
        ) : (
          <EmptyState
            icon={<PointOfSaleRoundedIcon sx={{ fontSize: 48 }} />}
            title={t('retailSettings.devices.emptyTitle')}
            description={t('retailSettings.devices.emptyDescription')}
            action={
              canManage ? (
                <Button
                  variant="contained"
                  startIcon={<PointOfSaleRoundedIcon />}
                  onClick={runImport}
                  disabled={importMutation.isPending}
                >
                  {t('retailSettings.devices.importButton')}
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('retailSettings.devices.table.device')}</TableCell>
                  <TableCell>{t('retailSettings.devices.table.evotorId')}</TableCell>
                  <TableCell>{t('retailSettings.devices.table.store')}</TableCell>
                  <TableCell>{t('retailSettings.devices.table.serialNumber')}</TableCell>
                  <TableCell>{t('retailSettings.devices.table.firmware')}</TableCell>
                  <TableCell>{t('retailSettings.devices.table.updated')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => {
                  const updatedAt = formatDateTime(device.updatedAt);
                  return (
                    <TableRow key={device.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {device.name || '—'}
                        </Typography>
                        {device.model ? (
                          <Typography variant="caption" color="text.secondary">
                            {device.model}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {device.evotorId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {device.storeName || device.storeEvotorId ? (
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Typography variant="body2">
                              {device.storeName || '—'}
                            </Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              {device.storeEvotorId ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                                >
                                  {device.storeEvotorId}
                                </Typography>
                              ) : null}
                              {device.storeActive === false ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="default"
                                  label={t('retailSettings.devices.storeInactiveChip')}
                                />
                              ) : null}
                            </Stack>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('retailSettings.devices.unassignedStore')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {device.serialNumber || '—'}
                        </Typography>
                        {device.imei ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            IMEI {device.imei}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{device.firmwareVersion || '—'}</TableCell>
                      <TableCell>{updatedAt ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <CompactTablePagination
            count={data?.page.totalElements ?? 0}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_event, next) => setPage(next)}
            onRowsPerPageChange={(event) => {
              setSize(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </Box>
  );
}
